import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE } from "../api/config";
import "./MonitoringWidget.css";

const tabs = [
  { key: "local", label: "Backend", icon: "💻" },
  { key: "synology", label: "Synology", icon: "🗄️" },
  { key: "ocr", label: "OCR-Server", icon: "🌐" },
  { key: "email", label: "Email", icon: "✉️" },
];

const dotClass = (s) => {
  if (s?.status === "online") {
    const m = Math.max(s.cpu_percent || 0, s.ram_percent || 0);
    if (m > 85) return "danger";
    if (m > 65) return "warn";
    return "ok";
  }
  if (s?.status === "offline") return "danger";
  return "off";
};

export default function MonitoringWidget() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("local");
  const [data, setData] = useState({});
  const [hidden, setHidden] = useState(false);

  // Hide on monitoring full-page
  useEffect(() => { setHidden(location.pathname.startsWith("/monitoring")); }, [location.pathname]);

  useEffect(() => {
    if (hidden) return;
    let cancelled = false;
    let unterwegs = false;   // nicht nachfragen, solange die letzte Antwort fehlt (sonst stauen sich Anfragen)
    const tick = async () => {
      if (unterwegs) return;
      unterwegs = true;
      try {
        const r = await fetch(`${API_BASE}/monitoring/all`);
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch (e) { /* */ }
      finally { unterwegs = false; }
    };
    tick();
    const interval = open ? 15000 : 60000;
    const id = setInterval(tick, interval);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, hidden]);

  if (hidden) return null;

  const cur = data[tab];

  return (
    <div className={`mw ${open ? "open" : ""}`}>
      {!open && (
        <button className="mw-pill" onClick={() => setOpen(true)} title="Server-Monitoring">
          <span>📊</span>
          {tabs.map(t => <span key={t.key} className={`mw-dot ${dotClass(data[t.key])}`} title={t.label} />)}
        </button>
      )}
      {open && (
        <div className="mw-panel">
          <div className="mw-head">
            <strong>Monitoring</strong>
            <div className="mw-head-btns">
              <button onClick={() => navigate("/monitoring")} title="Vollbild">⛶</button>
              <button onClick={() => setOpen(false)} title="Schließen">✕</button>
            </div>
          </div>
          <div className="mw-tabs">
            {tabs.map(t => {
              let lbl = t.label;
              if (t.key === "local" && data.local?.platform) {
                lbl = /macOS|Darwin/i.test(data.local.platform) ? "Lokal" : "Server";
              }
              return (
                <button key={t.key} className={`mw-tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
                  <span>{t.icon}</span>
                  <span className={`mw-dot ${dotClass(data[t.key])}`} />
                  {lbl}
                </button>
              );
            })}
          </div>
          <div className="mw-body">
            {!cur && <div className="mw-loading">Lade…</div>}
            {cur && cur.status === "not_configured" && <div className="mw-msg">Nicht konfiguriert<br/><small>{cur.error}</small></div>}
            {cur && cur.status === "offline" && <div className="mw-msg err">Offline<br/><small>{cur.error}</small></div>}
            {cur && cur.status === "online" && tab === "email" && (
              <>
                {cur.host && <div className="mw-host">{cur.host}</div>}
                {(cur.services || []).map((s, i) => (
                  <div key={i} className="mw-svc">
                    <span className={`mw-dot ${s.ok ? "ok" : "danger"}`} />
                    <span>{s.name} :{s.port}</span>
                    <span className="mw-svc-meta">{s.ok ? `${s.latency_ms} ms` : (s.error || "Fehler")}</span>
                  </div>
                ))}
              </>
            )}
            {cur && cur.status === "online" && tab !== "email" && (
              <>
                {cur.host && <div className="mw-host">{cur.host}</div>}
                <Mini label="CPU" v={cur.cpu_percent} />
                <Mini label="RAM" v={cur.ram_percent} />
                {cur.disks && cur.disks[0] && (
                  <Mini label={`Disk ${cur.disks[0].mount}`} v={cur.disks[0].percent} extra={`${(cur.disks[0].free_gb || 0).toFixed(1)} GB frei`} />
                )}
                {cur.idle && <div className="mw-pill-idle">💤 Ruhezustand</div>}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ label, v, extra }) {
  const cls = v > 85 ? "danger" : v > 65 ? "warn" : "ok";
  return (
    <div className="mw-mini">
      <div className="mw-mini-label">
        <span>{label}</span>
        <span>{v != null ? v.toFixed(0) + "%" : "—"}{extra ? ` · ${extra}` : ""}</span>
      </div>
      <div className="mw-mini-bar"><div className={`mw-mini-fill ${cls}`} style={{ width: `${Math.min(v || 0, 100)}%` }} /></div>
    </div>
  );
}
